import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import {useDebouncer} from "@tanstack/react-pacer";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useServerTableContext} from "../ServerTableContext";
import {
    useServerTableToolbarContext,
    useServerTableToolbarItemContext
} from "../ServerTableToolbarContext";
import {defineServerTableToolbarItem} from "../ServerTableToolbarItem";
import {serverTableControlIconSx, serverTableControlSx} from "../constants";
import type {ServerTableSearchProps} from "../types";

const SEARCH_DEBOUNCE_MS = 500;

const ServerTableSearch = ({
    placeholder
}: ServerTableSearchProps) => {
    const {t} = useTranslation();
    const {searchQuery: value, setSearchQuery: onChange} = useServerTableContext();
    const {
        closePanel,
        isSearchOpen,
        openSearch,
        closeSearch: collapseSearch
    } = useServerTableToolbarContext();
    const {presentation} = useServerTableToolbarItemContext();
    const resolvedPlaceholder = placeholder ?? t('Search');
    const [inputValue, setInputValue] = useState(value);
    const searchDebouncer = useDebouncer(
        onChange,
        {wait: SEARCH_DEBOUNCE_MS} as Parameters<typeof useDebouncer>[1]
    );
    const {cancel, maybeExecute} = searchDebouncer;

    useEffect(() => {
        cancel();
        setInputValue(value);
    }, [cancel, value]);

    const clearSearch = () => {
        cancel();
        setInputValue('');
        onChange('');
    };

    const closeSearch = () => {
        clearSearch();
        collapseSearch();
        closePanel();
    };

    const closeSearchLabel = t('Clear and close search');
    const searchField = (
        <TextField
            fullWidth
            type="text"
            size="small"
            value={inputValue}
            placeholder={resolvedPlaceholder}
            autoFocus
            onChange={event => {
                const nextValue = event.target.value;
                setInputValue(nextValue);
                maybeExecute(nextValue);
            }}
            onKeyDown={event => {
                if (event.key === 'Escape') {
                    event.stopPropagation();
                    closeSearch();
                }
            }}
            inputProps={{'aria-label': resolvedPlaceholder, role: 'searchbox'}}
            color="primary"
            sx={{
                '& .MuiOutlinedInput-root': {px: 1},
                '& .MuiOutlinedInput-input': {px: 0},
                '& .MuiInputAdornment-positionStart': {mr: 0.75},
                '& .MuiInputAdornment-positionEnd': {ml: 0.5}
            }}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start" sx={{color: 'primary.main'}}>
                        <SearchIcon fontSize="small"/>
                    </InputAdornment>
                ),
                endAdornment: (
                    <InputAdornment position="end" sx={{color: 'primary.main'}}>
                        <Tooltip title={closeSearchLabel}>
                            <IconButton
                                edge="end"
                                size="small"
                                color="primary"
                                data-dashboard-control=""
                                aria-label={closeSearchLabel}
                                onClick={closeSearch}
                            >
                                <CloseIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                    </InputAdornment>
                )
            }}
        />
    );

    const searchButton = (
        <Tooltip title={t('Search')}>
            <IconButton
                size="small"
                color="primary"
                data-dashboard-control=""
                aria-label={t('Search')}
                aria-expanded={isSearchOpen}
                onClick={() => {
                    closePanel();
                    openSearch();
                }}
                sx={{...serverTableControlSx, flexShrink: 0}}
            >
                <Badge color="primary" variant="dot" invisible={!value.trim()}>
                    <SearchIcon sx={serverTableControlIconSx}/>
                </Badge>
            </IconButton>
        </Tooltip>
    );

    // Hoisted into a page header, search has no row to grow into: the toolbar gives
    // it an overlay spanning the header and this renders the bare field into it.
    if (presentation === 'overlay') return searchField;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                flex: isSearchOpen ? '1 1 0' : '0 0 auto',
                minWidth: 0,
                ml: 'auto'
            }}
        >
            {isSearchOpen ? searchField : searchButton}
        </Box>
    );
};

defineServerTableToolbarItem(ServerTableSearch, {
    role: 'search'
});

export default ServerTableSearch;
